import React, { useEffect, useState } from "react";
import {
    TextField,
    Autocomplete,
    CircularProgress,
    Container,
    Typography,
    Button,Grid,Box,Card,CardContent,Divider,Chip,Paper,
} from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import EventSeatIcon from '@mui/icons-material/EventSeat';

const BusTicketBooking = () => {
    const [cities, setCities] = useState([]);
    const [fromCity, setFromCity] = useState(null);
    const [toCity, setToCity] = useState(null);
    const [journeyDate, setJourneyDate] = useState(dayjs().format("YYYY-MM-DD"));
    const [loading, setLoading] = useState(false);
    const [buses, setBuses] = useState([]);

        //  AM/PM
        const formatTime = (timeString) => {
            if (!timeString) return "";
            const [hours, minutes] = timeString.split(':');
            const hoursNum = parseInt(hours, 10);
            const period = hoursNum >= 12 ? 'PM' : 'AM';
            const displayHours = hoursNum % 12 || 12;
            return `${displayHours}:${minutes} ${period}`;
        };

            // Calculate duration
    const calculateDuration = (departureTime, arrivalTime) => {
        if (!departureTime || !arrivalTime) return "";
        
        const [depHours, depMinutes] = departureTime.split(':').map(Number);
        const [arrHours, arrMinutes] = arrivalTime.split(':').map(Number);
        
        let hoursDiff = arrHours - depHours;
        let minutesDiff = arrMinutes - depMinutes;
        
        if (minutesDiff < 0) {
            hoursDiff--;
            minutesDiff += 60;
        }
        
        if (hoursDiff < 0) {
            hoursDiff += 24;
        }
        
        return `${hoursDiff}h ${minutesDiff}m`;
    };

    // Fetch City 
    useEffect(() => {
        const fetchCities = async () => {
            try {
                setLoading(true);
                const jwtToken = sessionStorage.getItem("jwtToken");
                const response = await axios.get(
                    "https://api.ipaisa.co.in/myapp/bus/citylist",
                    { headers: { Authorization: `Bearer ${jwtToken}` } }
                );

                if (response.data && response.data.BusCities) {
                    setCities(response.data.BusCities);
                } else {
                    console.error("Invalid city list format:", response.data);
                }
            } catch (error) {
                console.error("Error fetching cities:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCities();
    }, []);

    // Search Bus Function
    const searchBuses = async () => {
        if (!fromCity || !toCity || !journeyDate) {
            toast.error("Please select From City, To City, and Date of Journey.");
            return;
        }

        const requestBody = {
            dateOfJourney: journeyDate,
            originId: fromCity.CityId,
            destinationId: toCity.CityId
        };

        try {
            setLoading(true);
            const jwtToken = sessionStorage.getItem("jwtToken");
            const response = await axios.post(
                "https://api.ipaisa.co.in/myapp/bus/search",
                requestBody,
                { headers: { Authorization: `Bearer ${jwtToken}` } }
            );

            if (response.data.BusSearchResult && response.data.BusSearchResult.BusResults) {
                setBuses(response.data.BusSearchResult.BusResults);
                toast.success("Buses found successfully!");
            } else {
                setBuses([]);
                toast.warning("No buses available for the selected route.");
            }
        } catch (error) {
            console.error("Error searching for buses:", error);
            toast.error("Failed to search buses.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="sm" style={{ textAlign: "center", marginTop: "20px" }}>
            <Typography variant="h5" gutterBottom>
                Bus Ticket Booking
            </Typography>

            {/* From  */}
            <Autocomplete
                options={cities}
                getOptionLabel={(option) => option.CityName}
                onChange={(event, newValue) => setFromCity(newValue)}
                value={fromCity}
                loading={loading}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="From City"
                        variant="outlined"
                        fullWidth
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            <br />

            {/* To City  */}
            <Autocomplete
                options={cities}
                getOptionLabel={(option) => option.CityName}
                onChange={(event, newValue) => setToCity(newValue)}
                value={toCity}
                loading={loading}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="To City"
                        variant="outlined"
                        fullWidth
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            <br />

            <TextField
                label="Date of Journey"
                type="date"
                value={journeyDate}
                onChange={(event) => setJourneyDate(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
            />

            <br />
            <br />

            {/* Search Button */}
            <Button
                variant="contained"
                color="primary"
                onClick={searchBuses}
                disabled={loading}
                fullWidth
            >
                {loading ? "Searching..." : "Search Bus"}
            </Button>

            <br />
            <br />

            {buses.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Paper 
                            elevation={5} 
                            sx={{ 
                                p: { xs: 2, md: 3 }, 
                                borderRadius: 3, 
                                background: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(10px)'
                            }}
                        >
                            <Typography 
                                variant="h5" 
                                sx={{ 
                                    fontWeight: 600, 
                                    mb: 3, 
                                    color: '#6a11cb',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <DirectionsBusIcon sx={{ mr: 1 }} />
                                Available Buses
                                <Chip 
                                    label={`${buses.length} found`} 
                                    size="small" 
                                    sx={{ ml: 2, bgcolor: '#e0f2fe', color: '#2575fc' }} 
                                />
                            </Typography>

                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    mb: 2,
                                    pb: 1,
                                    borderBottom: '1px solid #eee'
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {fromCity?.CityName} to {toCity?.CityName}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#555' }}>
                                    {new Date(journeyDate).toLocaleDateString('en-US', {
                                        weekday: 'short',
                                        month: 'short',
                                        day: 'numeric'
                                    })}
                                </Typography>
                            </Box>

                            <Grid container spacing={2}>
                                {buses.map((bus, index) => (
                                    <Grid item xs={12} key={index}>
                                        <Card 
                                            elevation={2} 
                                            sx={{ 
                                                borderRadius: 2,
                                                transition: 'transform 0.3s, box-shadow 0.3s',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: '0 8px 15px rgba(0,0,0,0.1)'
                                                }
                                            }}
                                        >
                                            <CardContent>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                            <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: '#2575fc' }}>
                                                                {bus.TravelName}
                                                            </Typography>
                                                            <Chip 
                                                                label={bus.BusType} 
                                                                size="small" 
                                                                sx={{ 
                                                                    bgcolor: bus.BusType.toLowerCase().includes('sleeper') ? '#fef3c7' : '#e0f2fe',
                                                                    color: bus.BusType.toLowerCase().includes('sleeper') ? '#d97706' : '#0369a1'
                                                                }} 
                                                            />
                                                        </Box>
                                                        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                                                            {bus.ServiceName}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid item xs={12} sm={4}>
                                                        <Box>
                                                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#6a11cb' }}>
                                                                {formatTime(bus.DepartureTime)}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                                Departure
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                        <Box sx={{ textAlign: 'center' }}>
                                                            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                                                                {calculateDuration(bus.DepartureTime, bus.ArrivalTime)}
                                                            </Typography>
                                                            <Box sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}>
                                                                <Box sx={{ height: '2px', width: '30px', bgcolor: '#ddd' }} />
                                                                <DirectionsBusIcon sx={{ mx: 1, color: '#2575fc', fontSize: 20 }} />
                                                                <Box sx={{ height: '2px', width: '30px', bgcolor: '#ddd' }} />
                                                            </Box>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                                        <Box>
                                                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#6a11cb' }}>
                                                                {formatTime(bus.ArrivalTime)}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                                Arrival
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <Divider sx={{ my: 1 }} />
                                                    </Grid>

                                                    <Grid item xs={6} sm={4}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <EventSeatIcon sx={{ color: '#2575fc', mr: 1, fontSize: 20 }} />
                                                            <Typography variant="body2">
                                                                {bus.AvailableSeats} Seats
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={6} sm={4} sx={{ textAlign: { xs: 'right', sm: 'center' } }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-end', sm: 'center' } }}>
                                                            <PriceCheckIcon sx={{ color: '#2575fc', mr: 1, fontSize: 20 }} />
                                                            <Typography variant="h6" component="p" sx={{ fontWeight: 700, color: '#6a11cb' }}>
                                                                ₹{bus.BusPrice.OfferedPriceRoundedOff}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>

                                                    <Grid item xs={12} sm={4}>
                                                        <Button
                                                            fullWidth
                                                            variant="contained"
                                                            sx={{
                                                                background: 'linear-gradient(45deg, #6a11cb 30%, #2575fc 90%)',
                                                                borderRadius: 2,
                                                                boxShadow: '0 3px 5px 2px rgba(106, 17, 203, .3)',
                                                                transition: 'all 0.3s',
                                                                '&:hover': {
                                                                    transform: 'translateY(-2px)',
                                                                    boxShadow: '0 6px 10px 4px rgba(106, 17, 203, .3)',
                                                                }
                                                            }}
                                                        >
                                                            Book Now
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </Box>
                )}

            <ToastContainer position="top-center" />
        </Container>
    );
};

export default BusTicketBooking;




